// Ranking service for tracking GMB positions across grid points (plain JS)
import { GooglePlacesService } from './dataforseo-places';
import {
  generateGridPointsWithAddresses,
  generateGridPoints,
  getGridLabel,
} from './geolocation';
import ApiService from '../../services/api';

import logger from '../../utils/logger';
export class RankingService {
  constructor(apiKey, useNewAPI = true, useLiveAPI = true) {
    this.placesService = new GooglePlacesService(apiKey, useNewAPI);
    this.useLiveAPI = useLiveAPI; // Flag to use live API endpoint
  }

  async performRankingAnalysis(config, onProgress) {
    logger.log('🎯 Starting Smart Radius Analysis with auto-expansion logic');
    logger.log('🔍 [HEATMAP RANKING DEBUG] Full config received:', config);
    logger.log('🔍 [HEATMAP RANKING DEBUG] Using Live API:', this.useLiveAPI);

    // If live API is enabled, use the backend endpoint
    if (this.useLiveAPI) {
      return this.performLiveAPIAnalysis(config, onProgress);
    }

    // Otherwise, use the original Google Places API logic
    return this.performGooglePlacesAnalysis(config, onProgress);
  }

  /**
   * Perform analysis using live backend API
   */
  async performLiveAPIAnalysis(config, onProgress) {
    logger.log('🚀 Using Live API endpoint for heatmap analysis');
    
    try {
      if (onProgress) onProgress(0, 100, 'Sending request to live API...');

      // Prepare request payload for live Visibeen API
      const requestData = {
        businessName: config.businessName,
        keyword: config.keyword,
        centerLat: Number(config.centerLocation?.lat),
        centerLng: Number(config.centerLocation?.lng),
        gridSize: config.gridSize || 5,
        radiusKm: config.searchRadius || 2,
        language: "en",
        device: "desktop"
      };

      logger.log('📤 Sending to live API:', requestData);
      logger.log('📤 Request as JSON:', JSON.stringify(requestData, null, 2));

      if (onProgress) onProgress(20, 100,);

      // Call live API
      const response = await ApiService.runMaps(requestData);

      logger.log('📥 Received response from live API:', response);

      if (onProgress) onProgress(90, 100, 'Processing results...');

      // Transform response to match expected format
      const transformedResult = this.transformLiveAPIResponse(response, config);

      if (onProgress) onProgress(100, 100, 'Analysis complete!');

      return transformedResult;
    } catch (error) {
      logger.error('❌ Live API error:', error);
      
      // Fallback to Google Places API if live API fails
      logger.warn('⚠️ Falling back to Google Places API');
      if (onProgress) onProgress(0, 100, 'Falling back to Google Places API...');
      
      return this.performGooglePlacesAnalysis(config, onProgress);
    }
  }

  /**
   * Transform live API response to match expected format
   */
  transformLiveAPIResponse(response, config) {
    logger.log('🔄 Transforming live API response:', response);
    
    // Check if response has data wrapper (DataForSEO API pattern)
    const apiData = response.data || response;
    
    // If response already has the expected structure, return it
    if (apiData.results && apiData.trackingId) {
      logger.log('✅ Response already in correct format');
      return apiData;
    }

    // Extract data from DataForSEO nested structure
    logger.log(`📊 Processing ${apiData.tasks?.length || 0} tasks from backend`);

    // Generate tracking ID
    const trackingId = `tracking_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const timestamp = new Date();

    // Reconstruct centerLocation from lat/lng if needed
    const centerLocation = {
      lat: config.centerLocation?.lat,
      lng: config.centerLocation?.lng
    };

    // Create grid results structure with proper coordinates for each grid point
    const gridSize = config.gridSize || 4;
    const searchRadius = config.searchRadius || 2;
    
    // Generate actual grid points with unique coordinates for each position
    const gridPoints = generateGridPoints(centerLocation, searchRadius, gridSize);
    
    // Process each task (one per grid point) with its unique results
    const gridResults = [];
    
    if (apiData.tasks && apiData.tasks.length > 0) {
      apiData.tasks.forEach((task, taskIndex) => {
        const taskData = task.data || {};
        const gridPosition = taskData.grid_position;
        
        // Extract businesses from this specific task
        let businesses = [];
        if (task.result && task.result.length > 0) {
          businesses = task.result[0].items || [];
        }
        
        // Transform businesses for this grid point
        const transformedBusinesses = businesses.map((item, index) => ({
          name: item.title,
          placeId: item.place_id,
          place_id: item.place_id,
          address: item.address,
          rating: item.rating?.value,
          reviewCount: item.rating?.votes_count,
          latitude: item.latitude,
          longitude: item.longitude,
          phone: item.phone,
          website: item.url,
          category: item.category,
          categories: [item.category, ...(item.additional_categories || [])],
          types: item.category_ids || [],
          photoCount: item.total_photos,
          workHours: item.work_hours,
          rank: item.rank_absolute || index + 1,
          originalRank: item.rank_absolute || index + 1,
          searchMethod: 'dataforseo_live_api'
        }));
        
        // Find corresponding grid point
        let gridPoint;
        if (gridPosition) {
          gridPoint = {
            lat: gridPosition.lat,
            lng: gridPosition.lng,
            id: gridPosition.cellId,
            gridX: gridPosition.j,
            gridY: gridPosition.i,
          };
        } else {
          gridPoint = gridPoints[taskIndex] || gridPoints[0];
        }
        
        const targetRank = this.#findBusinessRank(transformedBusinesses, config.businessName);
        
        gridResults.push({
          gridPoint: {
            lat: gridPoint.lat,
            lng: gridPoint.lng,
            id: gridPoint.id,
            gridX: gridPoint.gridX,
            gridY: gridPoint.gridY,
            address: config.businessAddress || 'Center Location',
            label: `${gridPoint.gridY},${gridPoint.gridX}`
          },
          businesses: transformedBusinesses,
          targetBusinessRank: targetRank,
          searchRadius: searchRadius * 1000,
          timestamp,
          keyword: taskData?.keyword || config.keyword
        });
        
        logger.log(`  ✅ Grid ${gridPoint.id}: ${businesses.length} businesses, rank: ${targetRank || 'Not found'}`);
      });
    } else {
      logger.warn('⚠️ No tasks from backend, using empty grid');
      gridPoints.forEach((gridPoint) => {
        gridResults.push({
          gridPoint: {
            lat: gridPoint.lat,
            lng: gridPoint.lng,
            id: gridPoint.id,
            gridX: gridPoint.gridX,
            gridY: gridPoint.gridY,
            address: config.businessAddress || 'Center Location',
            label: `${gridPoint.gridY},${gridPoint.gridX}`
          },
          businesses: [],
          targetBusinessRank: null,
          searchRadius: searchRadius * 1000,
          timestamp,
          keyword: config.keyword
        });
      });
    }

    const transformed = {
      trackingId,
      businessName: config.businessName,
      centerLocation,
      keyword: apiData.tasks?.[0]?.data?.keyword || config.keyword,
      gridSize: config.gridSize,
      searchRadius: config.searchRadius,
      results: gridResults,
      timestamp,
      averageRank: null,
      bestRank: null,
      worstRank: null,
      notRankedCount: 0,
      coverage: 0,
      targetPlaceId: config.targetPlaceId
    };

    logger.log('🔄 Transformed result:', {
      resultsCount: transformed.results?.length || 0,
      totalGridPoints: transformed.results?.length || 0,
      trackingId: transformed.trackingId,
      gridSize: transformed.gridSize
    });

    return transformed;
  }

  /**
   * Original Google Places API analysis logic
   */
  async performGooglePlacesAnalysis(config, onProgress) {
    logger.log('🔍 Using Google Places API for heatmap analysis');
    logger.log('🔍 [HEATMAP RANKING DEBUG] Full config received:', config);
    
    // Step 1: Auto-detect user's business primary category if not provided
    let primaryCategoryFilter = config.primaryCategoryFilter;
    logger.log('🔍 [DEBUG] Config check:', {
      businessName: config.businessName,
      targetPlaceId: config.targetPlaceId,
      primaryCategoryFilter: config.primaryCategoryFilter
    });
    
    if (!primaryCategoryFilter && (config.businessName || config.targetPlaceId)) {
      logger.log('🔍 ===== AUTO-DETECTING YOUR BUSINESS CATEGORY =====');
      primaryCategoryFilter = await this.detectUserBusinessCategory(config);
      if (primaryCategoryFilter) {
        logger.log(`🎯 ===== FOUND YOUR CATEGORY: ${primaryCategoryFilter.toUpperCase()} =====`);
        logger.log(`🏷️ Will now filter competitors to show only: ${primaryCategoryFilter}`);
      } else {
        logger.warn('⚠️ Could not detect your business category - showing all competitors');
      }
    } else if (primaryCategoryFilter) {
      logger.log(`🏷️ Using provided category filter: ${primaryCategoryFilter}`);
    } else {
      logger.warn('⚠️ No business name or Place ID provided for category detection');
    }
    
    // Step 2: Set category filter on the places service
    if (primaryCategoryFilter) {
      this.placesService.setPrimaryCategoryFilter(primaryCategoryFilter);
      logger.log(`🚫 ===== CATEGORY FILTERING ACTIVE: ${primaryCategoryFilter.toUpperCase()} =====`);
    } else {
      logger.log('ℹ️ No category filtering - showing all competitors');
    }

    // Optional coordinate fix example retained
    if (
      (config.businessName || '').includes('DigiWolves') ||
      (config.businessAddress || '').includes('Bestech Business Tower')
    ) {
      config.centerLocation = { lat: 30.6752, lng: 76.7409 };
    }

    if (onProgress) onProgress(0, 100, 'Generating grid points...');
    
    logger.log('🎯 CENTER LOCATION FOR GRID:', config.centerLocation);
    logger.log('📏 Search radius:', config.searchRadius, 'km');
    logger.log('📐 Grid size:', config.gridSize);
    
    const gridPoints = await generateGridPointsWithAddresses(
      config.centerLocation,
      config.searchRadius,
      config.gridSize,
      config.googleApiKey,
      false,
      (current, total, address) => {
        if (onProgress) {
          const pct = Math.floor((current / total) * 30);
          onProgress(pct, 100);
        }
      }
    );
    
    logger.log('✅ Grid points generated. First point:', gridPoints[0]);
    logger.log('✅ Grid points generated. Center point:', gridPoints[Math.floor(gridPoints.length / 2)]);

    const trackingId = `tracking_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const timestamp = new Date();
    const results = [];

    if (onProgress) onProgress(30, 100, 'Starting business search analysis...');

    for (let i = 0; i < gridPoints.length; i++) {
      const gridPoint = gridPoints[i];
      try {
        if (onProgress) {
          const pct = 30 + Math.floor(((i + 1) / gridPoints.length) * 60);
          const status = gridPoint.address
            ? `${gridPoint.address}`
            : `${gridPoint.id}`;
          onProgress(pct, 100, status);
        }

        const searchResult = await this.#searchAtGridPoint(
          gridPoint,
          config.keyword,
          config.businessName,
          config.businessAddress,
          config
        );
        results.push(searchResult);
        await new Promise((r) => setTimeout(r, 250));
      } catch (error) {
        logger.warn(`Search error at ${gridPoint.id}:`, error);
        results.push({
          gridPoint,
          businesses: [],
          targetBusinessRank: null,
          searchRadius: 1000,
          timestamp: new Date(),
          keyword: config.keyword,
        });
      }
    }

    if (onProgress) onProgress(95, 100, 'Calculating final statistics...');

    const stats = this.#calculateStatistics(results);

    if (onProgress) onProgress(100, 100, 'Analysis complete!');

    return {
      trackingId,
      businessName: config.businessName,
      centerLocation: config.centerLocation,
      keyword: config.keyword,
      gridSize: config.gridSize,
      searchRadius: config.searchRadius,
      results,
      timestamp,
      ...stats,
    };
  }

  async #searchAtGridPoint(gridPoint, keyword, targetBusinessName, businessAddress, config) {
    const searchRadiusKm = Math.max((config.searchRadius ?? 1), 0.5);
    const searchRadiusMeters = Math.round(searchRadiusKm * 1000);
    let businesses = [];

    try {
      const enhanced = await this.placesService.enhancedPlacesAPISearch(
        { lat: gridPoint.lat, lng: gridPoint.lng },
        searchRadiusKm,
        keyword,
        targetBusinessName,
        businessAddress ?? gridPoint.address,
        config.centerLocation,
        config.searchRadius,
        {
          depth: config.depth || 20,  // Pass depth from config
          searchThisArea: false,
          searchPlaces: true
        }
      );

      const seen = new Set();
      enhanced.forEach((business, idx) => {
        const key = business.placeId || business.place_id || `dataforseo_${idx}`;
        if (!seen.has(key)) {
          seen.add(key);
          businesses.push({ ...business, originalRank: business.originalRank ?? idx + 1 });
        }
      });
    } catch (error) {
      logger.warn('Enhanced search failed:', error);
    }

    if (businesses.length < 5) {
      try {
        const nearby = await this.placesService.nearbySearch(
          { lat: gridPoint.lat, lng: gridPoint.lng },
          searchRadiusMeters,
          keyword,
          {
            depth: config.depth || 20,  // Pass depth from config
            searchThisArea: false,
            searchPlaces: true
          }
        );

        const existing = new Set(businesses.map(b => b.placeId || b.place_id));
        nearby.forEach((business, idx) => {
          const key = business.placeId || business.place_id || `nearby_${idx}`;
          if (!existing.has(key)) {
            let withinRange = true;
            if (config.centerLocation && config.searchRadius &&
              typeof business.latitude === 'number' && typeof business.longitude === 'number') {
              const distance = this.#calculateDistance(
                config.centerLocation.lat,
                config.centerLocation.lng,
                business.latitude,
                business.longitude
              );
              const toleranceKm = 0.30;
              const maxRadiusWithTolerance = config.searchRadius + toleranceKm;
              if (distance > maxRadiusWithTolerance) {
                withinRange = false;
              }
              business.distanceFromCenter = distance;
            }

            if (withinRange) {
              businesses.push({ ...business, searchMethod: 'nearby_fallback', originalRank: business.originalRank ?? idx + 1 });
            }
          }
        });
      } catch (error) {
        logger.warn('Nearby search failed:', error);
      }
    }

    const methodPriority = {
      dataforseo_maps: 1,
      dataforseo_local_finder: 1,
      dataforseo_maps_nearby: 2,
      dataforseo_local_finder_nearby: 2,
      nearby_fallback: 3,
      dataforseo_details: 0,
      dataforseo: 5
    };

    businesses.sort((a, b) => {
      const ap = methodPriority[a.searchMethod] ?? 10;
      const bp = methodPriority[b.searchMethod] ?? 10;
      if (ap !== bp) return ap - bp;
      return (a.originalRank ?? 999) - (b.originalRank ?? 999);
    });

    if (this.placesService.primaryCategoryFilter) {
      businesses = this.placesService.filterByPrimaryCategory(businesses);
    }

    const targetBusinessRank = this.#findBusinessRank(businesses, targetBusinessName);

    return {
      gridPoint,
      businesses,
      targetBusinessRank,
      searchRadius: searchRadiusMeters,
      timestamp: new Date(),
      keyword
    };
  }

  #findBusinessRank(businesses, targetBusinessName) {
    if (!targetBusinessName) return null;
    const normalize = (s) => {
      if (!s || typeof s !== 'string') return '';
      return s.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
    };
    const target = normalize(targetBusinessName);
    if (!target) return null;
    
    for (let i = 0; i < businesses.length; i++) {
      const name = normalize(businesses[i]?.name || businesses[i]?.title || '');
      if (!name) continue;
      if (name === target || name.includes(target) || target.includes(name)) return i + 1;
    }
    return null;
  }

  #calculateStatistics(results) {
    const ranks = results.map((r) => r.targetBusinessRank).filter((r) => typeof r === 'number');
    const averageRank = ranks.length ? ranks.reduce((a, b) => a + b, 0) / ranks.length : null;
    const bestRank = ranks.length ? Math.min(...ranks) : null;
    const worstRank = ranks.length ? Math.max(...ranks) : null;
    const notRankedCount = results.filter((r) => r.targetBusinessRank == null).length;
    const coverage = results.filter((r) => r.targetBusinessRank !== null).length / results.length;
    return { averageRank, bestRank, worstRank, notRankedCount, coverage };
  }

  // Auto-detect user's business primary category
  async detectUserBusinessCategory(config) {
    try {
      let userBusiness = null;
      logger.log(' [DEBUG] Starting category detection with config:', config);

      // Method 1: Try to find by Place ID first (most accurate)
      if (config.targetPlaceId) {
        logger.log(` Looking up business by Place ID: ${config.targetPlaceId}`);
        try {
          userBusiness = await this.placesService.getPlaceDetails(config.targetPlaceId);
          logger.log(' Found business by Place ID:', userBusiness?.name);
          logger.log(' [DEBUG] Business data from Place ID:', userBusiness);
        } catch (error) {
          logger.warn(' Place ID lookup failed:', error.message);
        }
      }

      // Method 2: Search by business name and location if Place ID failed
      if (!userBusiness && config.businessName && config.centerLocation) {
        logger.log(` Searching for business: ${config.businessName}`);
        try {
          // Use textSearchNew or textSearchLegacy depending on API version
          const searchResults = this.placesService.useNewAPI 
            ? await this.placesService.textSearchNew(config.businessName, config.centerLocation, 500)
            : await this.placesService.textSearchLegacy({
                query: config.businessName,
                location: config.centerLocation,
                radius: 500
              });
          
          // Find the best match by name
          userBusiness = searchResults.find(business => 
            business.name.toLowerCase().includes(config.businessName.toLowerCase()) ||
            config.businessName.toLowerCase().includes(business.name.toLowerCase())
          );
          
          if (userBusiness) {
            logger.log(' Found business by search:', userBusiness.name);
            logger.log(' [DEBUG] Business data from search:', userBusiness);
          } else {
            logger.warn(' No matching business found in search results');
            logger.log(' [DEBUG] Search results:', searchResults);
          }
        } catch (error) {
          logger.warn(' Business name search failed:', error.message);
        }
      }

      // Extract primary category from the found business
      if (userBusiness) {
        let primaryCategory = null;

        // Priority 1: primaryType (most specific)
        if (userBusiness.primaryType) {
          primaryCategory = userBusiness.primaryType;
          logger.log(` Using primaryType: ${primaryCategory}`);
        }
        // Priority 2: First category in categories array
        else if (userBusiness.categories && userBusiness.categories.length > 0) {
          primaryCategory = userBusiness.categories[0];
          logger.log(` Using first category: ${primaryCategory}`);
        }
        // Priority 3: First type in types array
        else if (userBusiness.types && userBusiness.types.length > 0) {
          primaryCategory = userBusiness.types[0];
          logger.log(` Using first type: ${primaryCategory}`);
        }

        if (primaryCategory) {
          // Clean up the category (remove underscores, etc.)
          const cleanedCategory = primaryCategory.replace(/_/g, ' ').toLowerCase();
          logger.log(` Raw category: "${primaryCategory}" → Cleaned: "${cleanedCategory}"`);
          logger.log(` SUCCESS: Your business category detected as "${cleanedCategory}"`);
          return cleanedCategory;
        }
      }

      // Fallback: Try to detect category from business name
      if (config.businessName) {
        const businessName = config.businessName.toLowerCase();
        let detectedCategory = null;
        
        if (businessName.includes('electronics') || businessName.includes('electro')) {
          detectedCategory = 'electronics store';
        } else if (businessName.includes('hospital') || businessName.includes('medical')) {
          detectedCategory = 'hospital';
        } else if (businessName.includes('restaurant') || businessName.includes('food')) {
          detectedCategory = 'restaurant';
        } else if (businessName.includes('pharmacy') || businessName.includes('medical store')) {
          detectedCategory = 'pharmacy';
        } else if (businessName.includes('hotel') || businessName.includes('lodge')) {
          detectedCategory = 'lodging';
        }
        
        if (detectedCategory) {
          logger.log(`🏷️ FALLBACK: Detected category from business name: "${detectedCategory}"`);
          logger.log(`✅ SUCCESS: Your business category detected as "${detectedCategory}" (from name)`);
          return detectedCategory;
        }
      }

      logger.warn('🔍 Could not detect primary category for your business');
      return null;
    } catch (error) {
      logger.error('🔍 Error detecting business category:', error);
      return null;
    }
  }

  convertToGridDisplay(rankingData) {
    return rankingData.results.map((result) => ({
      gridPoint: result.gridPoint,
      rank: result.targetBusinessRank,
      color: this.#getRankingColor(result.targetBusinessRank),
      label: getGridLabel(result.gridPoint.gridX, result.gridPoint.gridY),
    }));
  }

  #getRankingColor(rank) {
    if (rank == null) return 'gray';
    if (rank <= 3) return 'green';
    if (rank <= 7) return 'yellow';
    return 'red';
  }

  #calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  async testApiConnection() {
    try {
      return await this.placesService.validateApiKey();
    } catch (_) {
      return false;
    }
  }

  validateConfig(config) {
    const errors = [];
    if (!config.businessName?.trim()) errors.push('Business name is required');
    if (!config.keyword?.trim()) errors.push('Search keyword is required');
    if (!config.centerLocation || typeof config.centerLocation.lat !== 'number' || typeof config.centerLocation.lng !== 'number') errors.push('Valid center coordinates are required');
    if (config.gridSize < 3 || config.gridSize > 10) errors.push('Grid size must be between 3 and 10');
    if (config.searchRadius <= 0 || config.searchRadius > 50) errors.push('Search radius must be between 0 and 50 kilometers');
    if (!config.googleApiKey?.trim()) errors.push('Google API key is required');
    return { isValid: errors.length === 0, errors };
  }
}
